<template>
  <div
    class="bb-table-wrapper"
    id="items-board"
  >
    <vue-good-table
      :key="tableRenderKey"
      :columns="headings"
      :fixed-header="false"
      :rows="items"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        perPage: 7,
        position: 'bottom',
        dropdownAllowAll: true,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Items per page',
        ofLabel: 'of',
        allLabel: 'All',
      }"
      :search-options="{
        enabled: true,
        placeholder: 'Search...',
        externalQuery: searchQuery,
      }"
      :row-style-class="rowStyleClassFn"
      @on-page-change="scheduleVerticalScrollRefresh"
      @on-per-page-change="scheduleVerticalScrollRefresh"
      v-on:cell-click="onCellClick"
      styleClass="vgt-table bordered"
      theme="black-rhino"
    >
      <template v-slot:table-row="props">
        <counter-dialog ref="counterDialog"></counter-dialog>
        <contents-dialog ref="contentsDialog"></contents-dialog>
        <attached-dialog ref="attachedDialog"></attached-dialog>
        <clear-dialog ref="clearDialog"></clear-dialog>
        <confirm-dialog ref="confirmDialog"></confirm-dialog>
        <delete-artefact-dialog
          ref="deleteArtefactDialog"
        ></delete-artefact-dialog>

        <!-- no heading is blank label -->
        <span v-if="props.column.label == ''"> </span>

        <!-- held_image: show exclamation only when held count exceeds the cap; otherwise show tick for held -->
        <span v-if="props.column.field === 'held_image'">
          <img
            v-if="props.row.over_held_limit"
            :src="exclamationImg"
            height="50"
            width="50"
            alt="held item over limit"
          />
          <img
            v-else-if="props.row.held"
            :src="tickImg"
            height="50"
            width="50"
            alt="held item"
          />
        </span>

        <!-- located_image: show exclamation when item has no valid location; otherwise show located tick -->
        <span v-else-if="props.column.field === 'located_image'">
          <img
            v-if="props.row.no_location === true"
            :src="exclamationImg"
            height="50"
            width="50"
            alt="item not located"
          />
          <img
            v-else
            :src="tickImg"
            height="50"
            width="50"
            alt="item located"
          />
        </span>

        <!-- usable_image: tick / cross / exclamation based on usable_status, with tooltip + orange dot on No/Partial -->
        <span v-else-if="props.column.field === 'usable_image'">
          <ToolTip
            v-if="
              props.row.usable_status !== 'Yes' && props.row.unusable_reasons
            "
            tableWrapperMode
            position="top"
            :text="props.row.unusable_reasons"
          >
            <span class="bb-usable-icon-wrapper">
              <img
                v-if="props.row.usable_status === 'Yes'"
                :src="tickImg"
                height="50"
                width="50"
                alt="usable weapon"
              />
              <img
                v-else-if="props.row.usable_status === 'No'"
                :src="crossImg"
                height="50"
                width="50"
                alt="unusable weapon"
              />
              <img
                v-else-if="props.row.usable_status === 'Partial'"
                :src="exclamationImg"
                height="50"
                width="50"
                alt="partially usable weapon"
              />
              <span class="bb-orange-circle"></span>
            </span>
          </ToolTip>
          <span v-else>
            <img
              v-if="props.row.usable_status === 'Yes'"
              :src="tickImg"
              height="50"
              width="50"
              alt="usable weapon"
            />
            <img
              v-else-if="props.row.usable_status === 'No'"
              :src="crossImg"
              height="50"
              width="50"
              alt="unusable weapon"
            />
            <img
              v-else-if="props.row.usable_status === 'Partial'"
              :src="exclamationImg"
              height="50"
              width="50"
              alt="partially usable weapon"
            />
          </span>
        </span>

        <span v-else-if="props.column.field === 'attached_image'">
          <span
            v-if="props.row.attached"
            class="d-inline-flex align-items-center justify-content-center"
            v-tooltip:top="getAttachedTooltipText(props.row)"
          >
            <img
              :src="tickImg"
              :class="isTickVisible(props)"
              height="50"
              width="50"
              alt="attached item"
            />
          </span>
          <img
            v-else
            :src="tickImg"
            :class="isTickVisible(props)"
            height="50"
            width="50"
            alt="attached item"
          />
        </span>

        <span v-else-if="props.column.field === 'attachment_count'">
          {{ getAttachmentHostCount(props.row) }}
        </span>

        <span v-else-if="props.column.field === 'attachment_items_image'">
          <img
            v-if="
              isTruthyFlag(props.row.attach_point) &&
              getAttachmentHostCount(props.row) > 0
            "
            class="bbActionIcon"
            :src="attachedImg"
            @click.stop="manageAttached(props.row)"
            alt="attached items image"
          />
        </span>

        <!-- other *_image flag fields: show tick when underlying flag true (exclude action/contents/held/usable icons) -->
        <span
          v-else-if="
            props.column.field.endsWith('_image') &&
            props.column.field !== 'adjust_image' &&
            props.column.field !== 'contents_image' &&
            props.column.field !== 'held_image' &&
            props.column.field !== 'usable_image' &&
            props.column.field !== 'attached_image' &&
            props.column.field !== 'attachment_items_image'
          "
        >
          <img
            :src="tickImg"
            :class="isTickVisible(props)"
            height="50"
            width="50"
            alt="maxed image"
          />
        </span>

        <!-- row number -->
        <span v-if="props.column.field === 'rowNumber'">
          {{
            props.row.originalIndex !== undefined
              ? props.row.originalIndex + 1
              : props.index + 1
          }}
        </span>

        <!-- show/hide tally quantity based on tallyable flag -->
        <span v-else-if="props.column.field === 'tally_qty'">
          {{
            isRowTallyable(props.row)
              ? (props.formattedRow[props.column.field] ?? '')
              : ''
          }}
        </span>

        <!-- Armour-specific: only show coverage/AP/HP/layer/dex_pen when item is equipped -->
        <span
          v-else-if="
            item_type === 'Armour' &&
            ['coverage', 'ap', 'hp', 'layer', 'dex_pen'].includes(
              props.column.field,
            )
          "
        >
          {{ props.row.equipped ? props.formattedRow[props.column.field] : '' }}
        </span>

        <!-- format enc -->
        <span v-else-if="props.column.field === 'enc'">
          {{
            props.row.enc !== null && props.row.enc !== undefined
              ? parseFloat(props.row.enc).toFixed(2)
              : ''
          }}
        </span>

        <!-- item image: render thumbnail instead of raw data URL/text, with default fallback -->
        <span v-else-if="props.column.field === 'image'">
          <img
            :src="resolveImageSrc(props.row['image'])"
            height="80"
            width="80"
            alt="item image"
          />
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>

        <!-- show/hide adjust image dep on tallyable -->
        <span
          v-if="
            props.column.field === 'adjust_image' && isRowTallyable(props.row)
          "
        >
          <img
            class="bbActionIcon"
            :src="adjustImg"
            @click.stop="adjustQuantity(props.row)"
            alt="adjust image"
          />
        </span>

        <!-- show/hide contents image dep on storage and content cnt > 0 -->
        <span
          v-if="
            props.column.field === 'contents_image' &&
            props.row.storage &&
            props.row.contents_count_direct > 0
          "
        >
          <img
            class="bbActionIcon"
            :src="contentsImg"
            @click.stop="manageContents(props.row)"
            alt="contents image"
          />
        </span>
      </template>

      <template #emptystate
        >*** Character has no items of this type ***</template
      >
    </vue-good-table>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'

import { VueGoodTable } from 'vue-good-table-next'
import AttachedDialog from './AttachedDialog.vue'
import CounterDialog from './CounterDialog.vue'
import ContentsDialog from './ContentsDialog.vue'
import ClearDialog from './ClearDialog.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import DeleteArtefactDialog from './DeleteArtefactDialog.vue'
import ToolTip from '../components/ToolTip.vue'
import { inject } from 'vue'

import buildAdjReasonsString from '../helpers/buildAdjReasonsString'

import exclamationImg from '../assets/images/bbExclamation2.png'
import attachedImg from '../assets/images/bbAttached.png'
import contentsImg from '../assets/images/bbContents.png'
import adjustImg from '../assets/images/bbAdjust.png'
import tickImg from '../assets/images/bbTick.png'
import crossImg from '../assets/images/bbBoxedCross.png'
import defaultImage from '../assets/images/default-item-image.png'

export default {
  name: 'TableItems',
  //==================================================================
  // components
  //------------
  components: {
    VueGoodTable,
    AttachedDialog,
    CounterDialog,
    ContentsDialog,
    ClearDialog,
    ConfirmDialog,
    DeleteArtefactDialog,
    ToolTip,
  },
  //==================================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //==================================================================
  // props
  //-------
  props: {
    headings: {
      type: Array,
      required: true,
    },
    item_type: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    character: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      required: false,
      default: '',
    },
  },
  //==================================================================
  // data
  //------
  data() {
    return {
      selectedValue: null,
      selectedColumn: null,
      selectedItem: null,
      isAdjustingClearTimer: null,
      //
      items_array: [],
      //
      attachedImg,
      exclamationImg,
      contentsImg,
      adjustImg,
      tickImg,
      crossImg,
      defaultImage,
      isAdjusting: false,
      verticalRefreshRaf: null,
      verticalRefreshTimeout: null,
      verticalScrollElement: null,
    }
  },
  //==================================================================
  // computed
  //----------
  computed: {
    tableRenderKey() {
      return this.headings
        .map((heading) => `${heading.field}:${heading.hidden ? 1 : 0}`)
        .join('|')
    },
    artefactStatusOptions() {
      const lookups = this.$store.getters['characters/getItemLookups'] || {}
      const statuses = Array.isArray(lookups.status) ? lookups.status : []

      return statuses
        .filter((status) => status && status !== 'n/a' && status !== 'Active')
        .map((status) => ({ value: status, label: status }))
    },
  },
  //====================================================================
  // methods
  //---------
  methods: {
    async confirmArtefactDeleteStatus(statuses, subject) {
      while (true) {
        const itemStatus = await this.$refs.deleteArtefactDialog.show({
          title: 'Delete Artefact',
          message: 'Confirm Reason',
          subject,
          cancelButton: 'Cancel',
          statuses,
        })

        if (!itemStatus) {
          return null
        }

        if (String(itemStatus).trim().toLowerCase() !== 'dropped') {
          return itemStatus
        }

        const ok = await this.$refs.confirmDialog.show({
          title: 'Delete Artefact',
          message:
            'Dropped artefacts can be pickup/added by any character after saving your character.',
          subject,
          okButton: 'OK',
          cancelButton: 'Cancel',
        })

        if (ok) {
          return itemStatus
        }
      }
    },
    resolveImageSrc(path) {
      if (!path) {
        return this.defaultImage
      }

      if (/^(https?:|data:|blob:)/i.test(path)) {
        return path
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const baseUrl =
        import.meta.env.VITE_API_URL ||
        (typeof window !== 'undefined'
          ? window.location.origin
          : 'http://localhost:3000')

      try {
        return new URL(normalizedPath, baseUrl).toString()
      } catch {
        return normalizedPath
      }
    },
    getVerticalScrollTargets() {
      const wrapper = document.getElementById('items-board')
      const responsive = this.$el?.querySelector('.vgt-responsive')

      return [wrapper, responsive].filter(
        (el, idx, arr) => !!el && arr.indexOf(el) === idx,
      )
    },
    getVerticalScrollElement() {
      const targets = this.getVerticalScrollTargets()
      if (targets.length === 0) {
        return null
      }

      let best = targets[0]
      let bestOverflow = Math.max(0, best.scrollHeight - best.clientHeight)

      targets.forEach((el) => {
        const overflow = Math.max(0, el.scrollHeight - el.clientHeight)
        if (overflow > bestOverflow) {
          best = el
          bestOverflow = overflow
        }
      })

      return best
    },
    getVerticalScrollState() {
      const targets = this.getVerticalScrollTargets()
      const epsilon = 0
      const targetState = targets.reduce(
        (acc, content) => {
          const top = content.scrollTop
          const maxTop = Math.max(
            0,
            content.scrollHeight - content.clientHeight,
          )

          return {
            canScrollDown: acc.canScrollDown || top < maxTop - epsilon,
            canScrollUp: acc.canScrollUp || top > epsilon,
          }
        },
        { canScrollDown: false, canScrollUp: false },
      )

      const footerEl =
        this.$el?.querySelector('.vgt-wrap__footer') ||
        this.$el?.querySelector('.vgt-pagination')
      const headerEl =
        this.$el?.querySelector('.vgt-table thead') ||
        this.$el?.querySelector('.vgt-global-search') ||
        this.$el

      const footerRect = footerEl?.getBoundingClientRect()
      const headerRect = headerEl?.getBoundingClientRect()

      const viewportState = {
        canScrollDown:
          !!footerRect && footerRect.bottom > window.innerHeight + 2,
        canScrollUp: !!headerRect && headerRect.top < -2,
      }

      return {
        canScrollDown: targetState.canScrollDown || viewportState.canScrollDown,
        canScrollUp: targetState.canScrollUp || viewportState.canScrollUp,
      }
    },
    bindVerticalScrollListener() {
      const nextElement = this.getVerticalScrollElement()
      if (!nextElement || this.verticalScrollElement === nextElement) {
        return
      }

      if (this.verticalScrollElement) {
        this.verticalScrollElement.removeEventListener(
          'scroll',
          this.scheduleVerticalScrollRefresh,
        )
      }

      this.verticalScrollElement = nextElement
      this.verticalScrollElement.addEventListener(
        'scroll',
        this.scheduleVerticalScrollRefresh,
        { passive: true },
      )
    },
    isTruthyFlag(value) {
      if (typeof value === 'boolean') {
        return value
      }

      if (typeof value === 'number') {
        return value === 1
      }

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return ['true', '1', 'yes', 'y', 'on'].includes(normalized)
      }

      return false
    },
    isRowTallyable(row) {
      const value = row?.tallyable

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return ['true', '1', 'yes', 'y', 'on'].includes(normalized)
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
    //==================================================================
    // is tick visible
    //-----------------
    isTickVisible(props) {
      const field = props.column.field
      if (!field || !field.endsWith('_image') || field === 'adjust_image') {
        return 'bb-img-hidden'
      }

      if (field === 'worn_image') {
        if (
          props.row &&
          (this.isTruthyFlag(props.row.worn) ||
            this.isTruthyFlag(props.row.equipped))
        ) {
          return 'bb-img-visible'
        }

        return 'bb-img-hidden'
      }

      const baseKey = field.replace(/_image$/, '')
      if (props.row && this.isTruthyFlag(props.row[baseKey])) {
        return 'bb-img-visible'
      }

      return 'bb-img-hidden'
    },
    //==================================================================
    // is exclamation visible
    //------------------------
    isExclamationVisible(props) {
      const field = props.column.field
      if (!field) {
        return 'bb-img-hidden'
      }

      if (props.row && props.row['no_location'] === true) {
        return 'bb-img-visible'
      }

      return 'bb-img-hidden'
    },
    //==================================================================
    // tooltip text for attached icon
    //--------------------------------
    getAttachedTooltipText(row) {
      if (!row || !row.attach_location) {
        return 'Attached'
      }

      const hostItem = Array.isArray(this.character?.items)
        ? this.character.items.find(
            (item) => item && item.id === row.attach_location,
          )
        : null

      const hostName = hostItem?.display_name || hostItem?.name || ''

      return hostName ? `Attached to ${hostName}` : 'Attached'
    },
    //==================================================================
    // count attached items for attach point
    //---------------------------------------
    getAttachmentHostCount(row) {
      if (!row || !this.isTruthyFlag(row.attach_point) || !row.id) {
        return ''
      }

      const items = Array.isArray(this.character?.items)
        ? this.character.items
        : []

      const hostId = String(row.id)

      return items.filter((item) => {
        if (
          !item ||
          item.attach_location === null ||
          item.attach_location === undefined
        ) {
          return false
        }

        return String(item.attach_location) === hostId
      }).length
    },
    //==================================================================
    // on cell click - open item sheet
    //----------------------------------
    onCellClick(params) {
      // --------------------------------------------------------------
      // prevent cell click if deleting or clicking from action column
      // --------------------------------------------------------------
      if (this.isAdjusting || params.column.field.substring(0, 6) == 'action') {
        return
      }
      // ---------------------------
      // show character item panel
      // ---------------------------
      this.$emit('show-character-item', params.row)
    },
    //==================================================================
    // colour rows
    //-------------
    rowStyleClassFn(row) {
      const rowType = row?.effective_type ?? row?.type

      // weapons fully unusable (usable_status 'No'): force black background
      if (
        (rowType === 'Melee Weapon' || rowType === 'Ranged Weapon') &&
        row.usable_status === 'No'
      ) {
        return 'rowUnusableWeapon'
      }
      // highlight rows with no location or broken items using the same warning colour
      if (row['no_location'] || row['broken']) {
        return 'rowWarning'
      } else if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      } else {
        return 'rowParty'
      }
    },
    getHorizontalScrollState() {
      const content = document.getElementById('items-board')
      if (!content) {
        return { canScrollLeft: false, canScrollRight: false }
      }

      const left = content.scrollLeft
      const maxLeft = Math.max(0, content.scrollWidth - content.clientWidth)
      const epsilon = 10

      return {
        canScrollLeft: left > epsilon,
        canScrollRight: left < maxLeft - epsilon,
      }
    },
    //==================================================================
    // handle scroll table
    //---------------------
    handleScrollTable(dir) {
      if (dir === 'up' || dir === 'down') {
        const targets = this.getVerticalScrollTargets()
        const activeTargets = targets.filter(
          (content) => content.scrollHeight - content.clientHeight > 2,
        )

        const footerEl =
          this.$el?.querySelector('.vgt-wrap__footer') ||
          this.$el?.querySelector('.vgt-pagination')
        const headerEl =
          this.$el?.querySelector('.vgt-table thead') ||
          this.$el?.querySelector('.vgt-global-search') ||
          this.$el

        const finalTops = new Map()

        if (activeTargets.length > 0) {
          activeTargets.forEach((content) => {
            const maxTop = Math.max(
              0,
              content.scrollHeight - content.clientHeight,
            )
            const currentTop = content.scrollTop
            const step = Math.max(Math.floor(content.clientHeight * 0.75), 220)

            let targetTop =
              dir === 'down'
                ? Math.min(maxTop, currentTop + step)
                : Math.max(0, currentTop - step)

            // Snap near the end to guarantee footer visibility.
            if (dir === 'down' && maxTop - targetTop < 28) {
              targetTop = maxTop
            }

            if (dir === 'up' && targetTop < 10) {
              targetTop = 0
            }

            content.scrollTo({
              top: targetTop,
              behavior: 'smooth',
            })

            finalTops.set(content, targetTop)
          })
        }

        // Always nudge the viewport too; in some layouts the visible right-side
        // scrollbar is the page, not the inner table wrapper.
        const viewportStep = Math.max(
          Math.floor(window.innerHeight * 0.55),
          180,
        )
        window.scrollBy({
          top: dir === 'down' ? viewportStep : -viewportStep,
          behavior: 'smooth',
        })

        this.scheduleVerticalScrollRefresh()

        if (this.verticalRefreshTimeout) {
          clearTimeout(this.verticalRefreshTimeout)
          this.verticalRefreshTimeout = null
        }

        // Smooth scrolling settles after first frame; refresh once settled.
        this.verticalRefreshTimeout = setTimeout(() => {
          finalTops.forEach((top, content) => {
            content.scrollTo({
              top,
              behavior: 'auto',
            })
          })

          // Only snap at the actual boundary, so middle rows are not skipped.
          const footerRect = footerEl?.getBoundingClientRect()
          const headerRect = headerEl?.getBoundingClientRect()

          if (
            dir === 'down' &&
            footerEl &&
            footerRect &&
            footerRect.bottom > window.innerHeight &&
            Array.from(finalTops.entries()).every(([content, top]) => {
              const maxTop = Math.max(
                0,
                content.scrollHeight - content.clientHeight,
              )
              return top >= maxTop - 2
            })
          ) {
            footerEl.scrollIntoView({ behavior: 'auto', block: 'end' })
          }

          if (
            dir === 'up' &&
            headerEl &&
            headerRect &&
            headerRect.top < 0 &&
            Array.from(finalTops.values()).every((top) => top <= 2)
          ) {
            headerEl.scrollIntoView({ behavior: 'auto', block: 'start' })
          }

          this.verticalRefreshTimeout = null
          this.$emit('refresh-vertical-scroll-state')
        }, 360)
        return
      }

      const content = document.getElementById('items-board')
      if (!content) {
        return
      }

      const maxLeft = Math.max(0, content.scrollWidth - content.clientWidth)
      const step = Math.max(Math.floor(content.clientWidth * 0.75), 240)
      const currentLeft = content.scrollLeft

      let targetLeft =
        dir === 'right'
          ? Math.min(maxLeft, currentLeft + step)
          : Math.max(0, currentLeft - step)

      if (targetLeft < 10) {
        targetLeft = 0
      }

      content.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      })
    },
    scheduleVerticalScrollRefresh() {
      if (this.verticalRefreshRaf !== null) {
        return
      }

      this.verticalRefreshRaf = window.requestAnimationFrame(() => {
        this.verticalRefreshRaf = null
        this.$emit('refresh-vertical-scroll-state')
      })
    },
    //==================================================================
    // manage contents
    //-----------------
    async manageContents(row) {
      console.log('manageContents...')

      const name = row['name'] || '' // item name e.g. 'Oil Flasks'
      console.log('manageContents row=', row)
      console.log('manageContents for name=', name)
      // ------------------------------------
      // prevent cell click whilst adjusting
      // ------------------------------------
      // this.isAdjusting = true
      // ---------------------
      // show contents dialog
      // ---------------------
      try {
        await this.$refs.contentsDialog.show({
          container: row,
          character: this.character,
        })
      } catch (error) {
        console.error('Error managing contents:', error)
      }
    },
    //==================================================================
    // manage attached items
    //----------------------
    async manageAttached(row) {
      if (this.getAttachmentHostCount(row) <= 0) {
        return
      }

      try {
        await this.$refs.attachedDialog.show({
          subjectItem: row,
          character: this.character,
        })
      } catch (error) {
        console.error('Error managing attached items:', error)
      }
    },
    //==================================================================
    // adjust quantity
    //-----------------
    async adjustQuantity(row) {
      console.log('adjustQuantity...')

      const name = row['name'] || '' // item name e.g. 'Oil Flasks'
      console.log('adjustQuantity row=', row)
      console.log('adjustQuantity for name=', name)
      // ------------------------------------
      // prevent cell click whilst adjusting
      // ------------------------------------
      this.isAdjusting = true
      // --------------------
      // show counter dialog
      // --------------------
      try {
        const newQty = await this.$refs.counterDialog.show({
          title: row['name'],
          label: 'Qty',
          row: row,
          character: this.character,
          okButton: 'OK',
          cancelButton: 'Cancel',
        })
        // ------------------------------------
        // newQty - call emit update item quantity
        // ------------------------------------
        if (newQty !== null && newQty !== undefined) {
          console.log('adjustQuantity OK for row=', newQty)
          //  set parms
          const key = 'tally_qty'
          // normalise to numeric so zero-check works even if
          // CounterDialog returns a string like '0'
          const value =
            typeof newQty === 'string' ? parseInt(newQty, 10) : newQty
          const id = row['id']

          const isArtefact = (row.actual_type ?? row.type) === 'Artefact'
          let item_status = null

          // If this is an artefact and tally is being set to zero,
          // warn that the item will be deleted.
          if (value === 0 && isArtefact) {
            item_status = await this.confirmArtefactDeleteStatus(
              this.artefactStatusOptions,
              row['display_name'] || row['name'],
            )

            if (!item_status) {
              // user aborted delete; do not proceed
              return
            }
          }

          // If setting tally_qty to zero and the item has
          // linked values, confirm via ClearDialog first.
          if (value === 0 && this.hasLinkedValues(row)) {
            const adjustments = this.buildAdjustmentReasonsForItem(id)

            const resp = await this.$refs.clearDialog.show({
              title: isArtefact
                ? 'Delete Artefact & Linked Values'
                : 'Clear Linked Values',
              subject: 'Tally quantity being set to zero:',
              message: isArtefact
                ? 'This will delete the artefact item and remove the following linked values:'
                : 'The following linked values will be removed from this item:',
              item: row,
              character: this.character,
              adjustments,
              okButton: isArtefact ? 'Delete' : 'OK',
              cancelButton: 'Cancel',
            })

            if (resp && resp.cancel) {
              // user aborted clear; do not emit update / delete
              return
            }
          }

          // For artefacts with tally set to zero, treat this as a
          // deletion rather than a simple tally update so that the
          // normal handleDeleteItem / processDeleteItem flow runs.
          if (value === 0 && isArtefact) {
            this.$emit('delete-character-item', {
              item: row,
              delete_payload: { status: item_status },
            })
            return
          }

          // either non-zero tally, no linked values,
          // or user confirmed the clear operation
          this.emitUpdate(key, value, name, id)
        }
      } finally {
        // -------------------------
        // clear any existing timer
        // -------------------------
        if (this.isAdjustingClearTimer) clearTimeout(this.isAdjustingClearTimer)
        // ----------------------------------
        // delay clearing isAdjusting (300ms)
        // ----------------------------------
        this.isAdjustingClearTimer = setTimeout(() => {
          this.isAdjusting = false
          this.isAdjustingClearTimer = null
        }, 300)
      }
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value, name, id) {
      // e.g. type = 'items', key = "tally_qty", value = 2, name = 'Oil Flasks', id = 123
      console.log(
        'emitUpdate key = ',
        key,
        ' value = ',
        value,
        ' name = ',
        name,
        ' id = ',
        id,
      )
      this.$emit('update-item-qty', { type: 'items', key, value, name, id })
    },
    //==================================================================
    // has linked values (mirrors ItemCard.needsClearing logic)
    //--------------------------------------------
    hasLinkedValues(row) {
      if (!row) return false

      // direct links on the item itself. We deliberately ignore
      // worn_location here because character worn locations are
      // fixed to the character and only the FK is nulled when
      // an item is cleared or deleted.
      if (row.attach_location || row.container) {
        return true
      }

      const character = this.character || {}
      const itemId = row.id
      if (!itemId) return false

      const runes = character.runes || []
      if (runes.some((r) => r.character_item === itemId)) {
        return true
      }

      const characteristic_adjs = character.characteristic_adjs || []
      if (characteristic_adjs.some((adj) => adj.character_item === itemId)) {
        return true
      }

      const ability_adjs = character.ability_adjs || []
      if (ability_adjs.some((adj) => adj.character_item === itemId)) {
        return true
      }

      const skill_adjs = character.skill_adjs || []
      if (skill_adjs.some((adj) => adj.character_item === itemId)) {
        return true
      }

      return false
    },
    //==================================================================
    // build adjustment reason strings for ClearDialog
    //----------------------------------------------
    buildAdjustmentReasonsForItem(itemId) {
      const character = this.character || {}

      const characteristic_adjs = character.characteristic_adjs || []
      const ability_adjs = character.ability_adjs || []
      const skill_adjs = character.skill_adjs || []

      const item_characteristic_adjs = characteristic_adjs.filter(
        (adj) => adj.character_item === itemId,
      )
      const item_ability_adjs = ability_adjs.filter(
        (adj) => adj.character_item === itemId,
      )
      const item_skill_adjs_applied = skill_adjs.filter(
        (adj) => adj.character_item === itemId && adj.applied == true,
      )
      const item_skill_adjs_optional = skill_adjs.filter(
        (adj) => adj.character_item === itemId && adj.applied == false,
      )

      return {
        item_characteristic_adj_reasons: item_characteristic_adjs.length
          ? buildAdjReasonsString(item_characteristic_adjs)
          : '',
        item_ability_adj_reasons: item_ability_adjs.length
          ? buildAdjReasonsString(item_ability_adjs)
          : '',
        item_skill_adj_reasons_applied: item_skill_adjs_applied.length
          ? buildAdjReasonsString(item_skill_adjs_applied)
          : '',
        item_skill_adj_reasons_optional: item_skill_adjs_optional.length
          ? buildAdjReasonsString(item_skill_adjs_optional)
          : '',
      }
    },
  },
  //==================================================================
  mounted() {
    this.bindVerticalScrollListener()
    window.addEventListener('scroll', this.scheduleVerticalScrollRefresh, {
      passive: true,
    })
    this.scheduleVerticalScrollRefresh()
  },
  //==================================================================
  updated() {
    this.bindVerticalScrollListener()
    this.scheduleVerticalScrollRefresh()
  },
  //==================================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // ------------------------
    // clear any pending timer
    // ------------------------
    if (this.isAdjustingClearTimer) {
      clearTimeout(this.isAdjustingClearTimer)
      this.isAdjustingClearTimer = null
    }

    if (this.verticalRefreshRaf !== null) {
      window.cancelAnimationFrame(this.verticalRefreshRaf)
      this.verticalRefreshRaf = null
    }

    if (this.verticalRefreshTimeout) {
      clearTimeout(this.verticalRefreshTimeout)
      this.verticalRefreshTimeout = null
    }

    if (this.verticalScrollElement) {
      this.verticalScrollElement.removeEventListener(
        'scroll',
        this.scheduleVerticalScrollRefresh,
      )
      this.verticalScrollElement = null
    }

    window.removeEventListener('scroll', this.scheduleVerticalScrollRefresh)
  },
}
</script>

<style scoped>
.rowUnusableWeapon {
  background-color: black !important;
  color: white;
}

.bb-usable-icon-wrapper {
  position: relative;
  display: inline-block;
}

.bb-usable-icon-wrapper .bb-orange-circle {
  position: absolute;
  top: 0;
  left: 100%;
  transform: none;
  margin-left: 0.6rem;
}
</style>
